import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AgGridAngular } from 'ag-grid-angular';
import { GridOptions } from 'ag-grid-community';
import moment from 'moment';
import { DashboardTimezoneInfo } from 'src/AngularJS/Admin/timezone.model';
import { AgentStateBucketColors, AgentStateBuckets, AgentStateDetails, SingleAgentState, SingleAgentStates, SummaryState, SummaryStateItem, getAgentStateColor } from 'src/app/reusables/models/agent-state-models';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { TimezoneService } from 'src/app/reusables/services/timezone.service';
import { AgentStateCellRendererComponent } from '../../modules/current-agent-states/agent-state-cell-renderer/agent-state-cell-renderer.component';
import { CurrentAgentStatesService } from '../../modules/current-agent-states/current-agent-states.service';

interface RowData {
    agentStateName: string;
    startTimeDisplayValue: number; // stored in ms
    startTimeDisplay: string;
    durationDisplayValue: number; // stored in seconds
    displayColor: string; // for the circle color
    displayStatus: string; // for the state name
    isDialer: boolean;
}

interface AgentModel {
    name: string;
    profilePicturePath: string;
}

@Component({
    selector: 'cv-agent-state-detail-cxone',
    templateUrl: './agent-state-detail-cxone.component.html',
    styleUrls: ['./agent-state-detail-cxone.component.scss']
})
export class AgentStateDetailCXOneComponent implements OnInit {
    @ViewChild('agentStateDetailGrid') agGrid: AgGridAngular;
    @Input() selectedRow: SingleAgentState;
    @Input() moduleKey: string;
    @Input() groupID: number;
    @Input() modalOpen: boolean;
    @Input() dashKey: string;

    public data: SingleAgentStates;
    public lastUpdatedTimestamp = "";
    public gridOptions: GridOptions<any>;
    public noData = false;
    public agentStateCategoriesReady = false;
    public isLoading = true;
    public today: Date = null;

    public agent: AgentModel = {
        name: '',
        profilePicturePath: '',
    };
    public tableData: RowData[] = [];
    public stateSummary: SummaryState = {
        loggedOut: { durationString: '', durationSeconds: 0, percentage: 0, stateName: '' },
        available: { durationString: '', durationSeconds: 0, percentage: 0, stateName: '' },
        availableDialer: { durationString: '', durationSeconds: 0, percentage: 0, stateName: '' },
        workingContacts: { durationString: '', durationSeconds: 0, percentage: 0, stateName: '' },
        workingContactsDialer: { durationString: '', durationSeconds: 0, percentage: 0, stateName: '' },
        totalUnavailable: { durationString: '', durationSeconds: 0, percentage: 0, stateName: '' },
        unavailableStates: [] as SummaryStateItem[],
        summaryTotal: { durationString: '', durationSeconds: 0, percentage: 0, stateName: '' },
    };

    // Used for mapping localization keys
    public AgentStateCategories: { [key: string]: string } = {
        "Logged Out": "Logged_Out",
        Available: "Available",
        "Available & Dialer": "Available_Dialer",
        "Working Contacts": "Working_Contacts",
        "Working Contacts & Dialer": "Working_Contacts_Dialer",
        Unavailable: "Unavailable",
        "Logged In": "Logged_In",
    };

    constructor(
        private agGridHelperService: AgGridHelperService,
        private cvLocalizationService: CVLocalizationService,
        private service: CurrentAgentStatesService,
        private activeModal: NgbActiveModal,
        private timezoneService: TimezoneService,
    ) { }

    ngOnInit(): void {
        this.initializeComponentAsync();
    }

    async initializeComponentAsync() {
        this.agent.name = this.selectedRow?.GroupName;
        this.agent.profilePicturePath = '../Admin/ImageResizer.aspx?imgid=-2&w=56&h=56'; //Default to a generic profile image
        await this.prepareTodayDate();
        this.cvLocalizationService.GetSection('Agent_State_Details').finally(() => this.setupGrid());
        //Set the values in a $timeout, forcing values to update after element is rendered in the DOM.
        //  This allows a "No Value to Actual Value" transition to occur, for visual fun.
        setTimeout(() => {
            //Call loadData
            this.loadData();
        }, 500);
    }

    async prepareTodayDate(): Promise<void> {
        try {
            // retrieving timezone set in dashboard or user or company, if all are null offsetSeconds will default to the companys MSTOffset
            const tzi = await this.timezoneService.getDashUserCompTimezone(this.dashKey, this.moduleKey) as DashboardTimezoneInfo;
            // removing browser timezone
            const now = new Date();
            const utc = new Date(now.getTime() + now.getTimezoneOffset() * 60000);
            // adding back in the dashboard timezone and setting max and min dates selectable
            this.today = new Date(utc).addSeconds(Number(tzi.TimezoneOffsetSeconds));

            this.today.setHours(23, 59, 59, 999);
        } catch (error) {
            throw error;
        }
    }

    //#region Ag-Grid
    public setupGrid(): void {
        const millisecondComparator = (valueA: string, valueB: string, nodeA: any, nodeB: any) =>
            nodeA.data.startTimeDisplayValue > nodeB.data.startTimeDisplayValue ? 1
                : nodeA.data.startTimeDisplayValue < nodeB.data.startTimeDisplayValue ? -1
                    : 0;

        this.gridOptions = this.agGridHelperService.createGridOptions({
            defaultColDef: {
                sortable: true,
                suppressMenu: true,
                icons: {
                    sortAscending: '<div class="sortIcons"><svg class="icon-10 fill-cxoneBlue"><use href="../App_Themes/CXone/icons/arrow-up.svg#arrow-up"></use></svg><svg class="icon-10 fill-sort"><use href="../App_Themes/CXone/icons/arrow-down.svg#arrow-down"></use></svg></div>',
                    sortDescending: '<div class="sortIcons"><svg class="icon-10 fill-sort"><use href="../App_Themes/CXone/icons/arrow-up.svg#arrow-up"></use></svg><svg class="icon-10 fill-cxoneBlue"><use href="../App_Themes/CXone/icons/arrow-down.svg#arrow-down"></use></svg></div>',
                    filter: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/filter.svg#filter"></use></svg>',
                    menu: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/widget-menu.svg#widget-menu"></use></svg>',
                    columns: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/chart.svg#chart"></use></svg>',
                },
            },
            suppressContextMenu: true,
            suppressMovableColumns: true,
            headerHeight: 44,
            rowHeight: 36,
            rowClass: 'cx-one-ui',
            components: {
                AgentStateCellRenderer: AgentStateCellRendererComponent,
            },
            overlayNoRowsTemplate: `<div class="error-container">
                                        <svg class="error-icon">
                                            <use href="../App_Themes/CXone/icons/empty.svg#empty"></use>
                                        </svg>
                                        <p class="cxone-error-text">${this.cvLocalizationService.getCachedText("Agent_State_Details", "No_Data_Available", "No data available")}</p>
                                    </div>` ,
            columnDefs: [
                {
                    field: "agentStateName",
                    colId: "agentStateName",
                    cellRenderer: 'AgentStateCellRenderer',
                    // this cell renderer is used in both agent state detail and the main table. this function below is used in the table
                    // and so must be included here as well to not break the cell renderer.
                    cellRendererParams: {
                        getColorMap: () => false
                    },
                    headerName: this.cvLocalizationService.getCachedText("Agent_State_Details", "Agent_State", "Agent State"),
                    minWidth: 230,
                    flex: 1.5,
                },
                {
                    field: "startTimeDisplay",
                    colId: "startTimeDisplayValue",
                    headerName: this.cvLocalizationService.getCachedText("Agent_State_Details", "Header_Start_Time", "Start Time"),
                    minWidth: 150,
                    comparator: (valueA, valueB, nodeA, nodeB) => millisecondComparator(valueA, valueB, nodeA, nodeB),
                    flex: 1,
                },
                {
                    field: "durationDisplayValue",
                    colId: "durationDisplayValue",
                    headerName: this.cvLocalizationService.getCachedText("Agent_State_Details", "CXone_Duration", "Duration"),
                    minWidth: 150,
                    valueFormatter: params => this.convertTimeFromSeconds(params.data.durationDisplayValue),
                    flex: 1,
                }
            ],
            onGridReady: () => {
                if (this.tableData.length > 0)
                    this.agGrid?.api.setGridOption('rowData', this.tableData);
            },
        });
    }
    //#endregion

    //#region Data Load/Set
    async loadData(): Promise<void> {
        this.isLoading = true;

        this.data = await this.service.getSingleAgentStates(this.moduleKey, this.groupID, this.selectedRow?.UserID, this.selectedRow?.AgentSessionID, this.getFormattedDate(), true);
        this.agent.profilePicturePath = this.data.ImagePath;

        // Set summary values
        this.setInitialStateSummaries(); // Reset the state summaries. Specifically to handle data refreshes and dataset changes.
        this.setStateSummary();

        this.setTableData(); // Fills tableData to use in table

        if (this.gridOptions != null && this.agGrid?.api != null) {
            this.agGrid?.api.setGridOption('rowData', this.tableData);
        }

        this.lastUpdatedTimestamp = this.data.LastUpdatedDateTime;
        this.isLoading = false;
        this.agentStateCategoriesReady = true;
        this.noData = this.tableData.length === 0;
    }

    public setTableData(): void {
        this.tableData = this.data.Details.map((state: AgentStateDetails) => ({
            agentStateName: state.DisplayStateName,
            startTimeDisplay: state.ActivityStartTimeDisplay,
            startTimeDisplayValue: this.getMiliFromDateTime(state.DtAgentSessionActivityStartTimestamp),
            durationDisplayValue: state.Duration,
            displayColor: this.getColorForAgentStateType(state.AgentStateNo, state.Dialer),
            displayStatus: state.DisplayStateName,
            isDialer: state.Dialer
        }));
    }

    public setStateSummary(): void {
        const totalTimeBuckets = {
            loggedOut: 0,
            available: 0,
            availableDialer: 0,
            workingContacts: 0,
            workingContactsDialer: 0,
            unavailable: 0,
        };

        this.data.Details.forEach((state: AgentStateDetails) => {
            switch (state.AgentStateNo) {
                case AgentStateBuckets.LoggedOut:
                    totalTimeBuckets.loggedOut += state.Duration;
                    break;
                case AgentStateBuckets.Available:
                    if (state.Dialer) {
                        totalTimeBuckets.availableDialer += state.Duration;
                    } else {
                        totalTimeBuckets.available += state.Duration;
                    }
                    break;
                case AgentStateBuckets.WorkingContacts:
                    if (state.Dialer) {
                        totalTimeBuckets.workingContactsDialer += state.Duration;
                    } else {
                        totalTimeBuckets.workingContacts += state.Duration;
                    }
                    break;
                case AgentStateBuckets.Unavailable: // All unavailable states have the same state number
                    totalTimeBuckets.unavailable += state.Duration;

                    let customUnavailableState = this.stateSummary.unavailableStates.find(unavailableState => unavailableState.stateName === state.DisplayStateName)
                    if (customUnavailableState) {
                        customUnavailableState.durationSeconds += state.Duration;
                        customUnavailableState.durationString = this.convertTimeFromSeconds(customUnavailableState.durationSeconds);
                        this.stateSummary.unavailableStates.map(unavailableState => unavailableState.stateName === state.DisplayStateName ? customUnavailableState : unavailableState);
                    } else {
                        this.stateSummary.unavailableStates.push({
                            stateName: state.DisplayStateName,
                            durationString: this.convertTimeFromSeconds(state.Duration),
                            durationSeconds: state.Duration,
                            percentage: 0
                        });
                    }
                    break;
            }
        });

        // Set summary values
        this.stateSummary.loggedOut = {
            durationString: this.convertTimeFromSeconds(totalTimeBuckets.loggedOut),
            durationSeconds: totalTimeBuckets.loggedOut,
            percentage: 0,
            stateName: this.cvLocalizationService.getCachedText("Agent_State_Details", "Logged_Out", "Logged Out")
        };

        this.stateSummary.available = {
            durationString: this.convertTimeFromSeconds(totalTimeBuckets.available),
            durationSeconds: totalTimeBuckets.available,
            percentage: 0,
            stateName: this.cvLocalizationService.getCachedText("Agent_State_Details", "Available", "Available")
        };

        this.stateSummary.availableDialer = {
            durationString: this.convertTimeFromSeconds(totalTimeBuckets.availableDialer),
            durationSeconds: totalTimeBuckets.availableDialer,
            percentage: 0,
            stateName: this.cvLocalizationService.getCachedText("Agent_State_Details", "Available_Dialer", "Available & Dialer")
        };

        this.stateSummary.workingContacts = {
            durationString: this.convertTimeFromSeconds(totalTimeBuckets.workingContacts),
            durationSeconds: totalTimeBuckets.workingContacts,
            percentage: 0,
            stateName: this.cvLocalizationService.getCachedText("Agent_State_Details", "Working_Contacts", "Working Contacts")
        };

        this.stateSummary.workingContactsDialer = {
            durationString: this.convertTimeFromSeconds(totalTimeBuckets.workingContactsDialer),
            durationSeconds: totalTimeBuckets.workingContactsDialer,
            percentage: 0,
            stateName: this.cvLocalizationService.getCachedText("Agent_State_Details", "Working_Contacts_Dialer", "Working Contacts & Dialer")
        };

        this.stateSummary.totalUnavailable = {
            durationString: this.convertTimeFromSeconds(totalTimeBuckets.unavailable),
            durationSeconds: totalTimeBuckets.unavailable,
            percentage: 0,
            stateName: this.cvLocalizationService.getCachedText("Agent_State_Details", "Total_Unavailable", "Total Unavailable")
        };

        // Set summary totals
        this.stateSummary.summaryTotal.durationSeconds =
            // this.stateSummary.loggedOut.durationSeconds + // Logged out is not included in the total
            this.stateSummary.available.durationSeconds +
            this.stateSummary.availableDialer.durationSeconds +
            this.stateSummary.workingContacts.durationSeconds +
            this.stateSummary.workingContactsDialer.durationSeconds +
            this.stateSummary.totalUnavailable.durationSeconds;

        this.stateSummary.summaryTotal.durationString = this.convertTimeFromSeconds(this.stateSummary.summaryTotal.durationSeconds);

        // Set percentage values
        this.setSummaryStatePercentages();
    }

    public setSummaryStatePercentages(): void {
        this.stateSummary.loggedOut.percentage = this.calculatePercentage(this.stateSummary.loggedOut.durationSeconds, this.stateSummary.summaryTotal.durationSeconds);
        this.stateSummary.available.percentage = this.calculatePercentage(this.stateSummary.available.durationSeconds, this.stateSummary.summaryTotal.durationSeconds);
        this.stateSummary.availableDialer.percentage = this.calculatePercentage(this.stateSummary.availableDialer.durationSeconds, this.stateSummary.summaryTotal.durationSeconds);
        this.stateSummary.workingContacts.percentage = this.calculatePercentage(this.stateSummary.workingContacts.durationSeconds, this.stateSummary.summaryTotal.durationSeconds);
        this.stateSummary.workingContactsDialer.percentage = this.calculatePercentage(this.stateSummary.workingContactsDialer.durationSeconds, this.stateSummary.summaryTotal.durationSeconds);
        this.stateSummary.totalUnavailable.percentage = this.calculatePercentage(this.stateSummary.totalUnavailable.durationSeconds, this.stateSummary.summaryTotal.durationSeconds);

        this.stateSummary.unavailableStates.forEach((state: SummaryStateItem) => {
            state.percentage = this.calculatePercentage(state.durationSeconds, this.stateSummary.summaryTotal.durationSeconds);
        });
    }
    //#endregion

    //#region Helper Functions
    public getFormattedDate(): string {
        //Endpoint expects date in format: Year, Month, Date
        //return ''.concat(ctrl.selectedDate.getFullYear(), '-', ctrl.selectedDate.getMonth()+1, '-', ctrl.selectedDate.getDate());
        const currentDate = new Date(this.today);
        return currentDate.getFullYear() + '-' + (currentDate.getMonth() + 1) + '-' + currentDate.getDate();

    };

    public getColorForAgentStateType(agentStateNo: number, isDialer: boolean): string {
        switch (agentStateNo) {
            case AgentStateBuckets.LoggedOut:
                return getAgentStateColor(AgentStateBucketColors.LoggedOut);
            case AgentStateBuckets.Available:
                return getAgentStateColor(isDialer ? AgentStateBucketColors.AvailableDialer : AgentStateBucketColors.Available);
            case AgentStateBuckets.WorkingContacts:
                return getAgentStateColor(isDialer ? AgentStateBucketColors.WorkingContactsDialer : AgentStateBucketColors.WorkingContacts);
            case AgentStateBuckets.Unavailable:
                return getAgentStateColor(AgentStateBucketColors.Unavailable);
        }
    }

    // need to convert to miliseconds for sorting to work in agGrid correctly
    public getMiliFromDateTime(timestamp: string): number {
        return new Date(timestamp).getTime();
    }

    convertTimeFromSeconds(seconds) {
        if (!Number.isInteger(Number(seconds))) {
            // For the millisecond case, we need to use milliseconds.
            return moment.utc().startOf('day').milliseconds(seconds * 1000).format('HH:mm:ss.SSS').toString();
            
        } else {
            return moment.utc().startOf('day').seconds(seconds).format('HH:mm:ss').toString();
        }
    }

    public close(): void {
        this.activeModal.close();
    }

    // Render values after element exists in DOM
    public setInitialStateSummaries(): void {
        this.stateSummary = {
            loggedOut: { durationString: '', durationSeconds: 0, percentage: 0, stateName: '' },
            available: { durationString: '', durationSeconds: 0, percentage: 0, stateName: '' },
            availableDialer: { durationString: '', durationSeconds: 0, percentage: 0, stateName: '' },
            workingContacts: { durationString: '', durationSeconds: 0, percentage: 0, stateName: '' },
            workingContactsDialer: { durationString: '', durationSeconds: 0, percentage: 0, stateName: '' },
            totalUnavailable: { durationString: '', durationSeconds: 0, percentage: 0, stateName: '' },
            unavailableStates: [] as SummaryStateItem[],
            summaryTotal: { durationString: '', durationSeconds: 0, percentage: 0, stateName: '' },
        };
    }

    public convertTimeStringToSeconds(timeString: string): number {
        const [hours, minutes, seconds] = timeString.split(':').map(Number);
        return (hours * 3600) + (minutes * 60) + seconds;
    }

    public calculatePercentage(value: number, total: number): number {
        if (total === 0) {
            return 0; // To avoid division by zero
        }
        return parseFloat(((value / total) * 100).toFixed(2));
    }
}
